import { useToast } from '@/components/ui';
import { useConnection } from '@/features/connections/connection-context';
import { DEFAULT_QUERY_LIMIT } from '@shared/constants';
import type { Filter, OrderBy } from '@shared/types';
import { useEffect, useMemo, useRef, useState } from 'react';
import type { SavedQuery } from '../../types';
import { useQueryBuilder } from '../query-builder-store';
import { buildFirestoreQuery } from '../query-builder-store/query-builder-store.domain';
import {
  clearDefaultQuery,
  deleteQuery,
  getDefaultQueryName,
  loadQueries,
  loadQuery,
  saveQuery,
  setDefaultQuery,
} from '../query-storage';
import {
  buildQueryFromState,
  defaultSaveQueryName,
  isOrErrorRelevant,
  isRunQueryDisabled,
} from './query-builder-panel.domain';

export interface QueryBuilderPanelProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  collectionName: string;
  availableFields: string[];
  /** When provided (e.g. from Explorer), clear uses this so state + refetch are handled in one place */
  onClearQuery?: () => void;
}

export function useQueryBuilderPanel({
  open,
  onOpenChange,
  collectionName,
  availableFields,
  onClearQuery,
}: QueryBuilderPanelProps) {
  const { connectionId } = useConnection();
  const { toast } = useToast();
  const {
    getState,
    addCondition,
    removeCondition,
    updateCondition,
    addOrCondition,
    removeOrCondition,
    updateOrCondition,
    addOrderBy,
    removeOrderBy,
    updateOrderBy,
    setLimit,
    setDocumentIdPrefix,
    clearQuery,
    executeTestRun,
    executeQuery,
    applyQueryToCollection,
  } = useQueryBuilder();

  const state = collectionName ? getState(collectionName) : null;
  const conditions = state?.conditions ?? [];
  const orConditions = state?.orConditions ?? [];
  const orderBy = state?.orderBy ?? [];
  const limit = state?.limit ?? DEFAULT_QUERY_LIMIT;
  const documentIdPrefix = state?.documentIdPrefix ?? '';
  const activeQuery = state?.activeQuery ?? null;
  const queryResults = state?.queryResults ?? null;
  const loading = state?.loading ?? false;
  const error = state?.error ?? null;
  const testRunLoading = state?.testRunLoading ?? false;
  const testRunError = state?.testRunError ?? null;

  const [savedQueries, setSavedQueries] = useState<SavedQuery[]>([]);
  const [saveQueryName, setSaveQueryName] = useState('');
  const [queryPreviewOpen, setQueryPreviewOpen] = useState(false);

  const activeFilterCount = useMemo(() => {
    const valid = (c: Filter) =>
      Boolean(c.field && c.value !== '' && c.value !== null && c.value !== undefined);
    const andCount = conditions.filter(valid).length;
    const orCount = orConditions.length >= 2 ? orConditions.filter(valid).length : 0;
    return andCount + orCount;
  }, [conditions, orConditions]);

  const defaultQueryName =
    connectionId && collectionName ? getDefaultQueryName(connectionId, collectionName) : null;

  const sortedSavedQueries = useMemo(() => {
    if (!defaultQueryName) return savedQueries;
    const idx = savedQueries.findIndex((q) => q.name === defaultQueryName);
    if (idx <= 0) return savedQueries;
    const copy = [...savedQueries];
    const [defaultQ] = copy.splice(idx, 1);
    return [defaultQ, ...copy];
  }, [savedQueries, defaultQueryName]);

  useEffect(() => {
    if (connectionId && collectionName) {
      setSavedQueries(loadQueries(connectionId, collectionName));
    } else {
      setSavedQueries([]);
    }
  }, [connectionId, collectionName]);

  const prevOpenRef = useRef(open);
  useEffect(() => {
    if (open && !prevOpenRef.current && defaultQueryName) {
      setSaveQueryName(defaultQueryName);
    }
    prevOpenRef.current = open;
  }, [open, defaultQueryName]);

  const queryDefSignature = useMemo(
    () =>
      JSON.stringify({
        conditions,
        orConditions,
        orderBy,
        documentIdPrefix,
      }),
    [conditions, orConditions, orderBy, documentIdPrefix]
  );

  const isNativeQuery = useMemo(() => {
    const valid = (c: Filter) =>
      Boolean(c.field && c.value !== '' && c.value !== null && c.value !== undefined);
    const hasFilters =
      conditions.some(valid) || (orConditions.length >= 2 && orConditions.some(valid));
    const hasOrderBy = orderBy.some((o) => Boolean(o.field));
    const hasDocumentIdPrefix = Boolean(documentIdPrefix?.trim());
    return !hasFilters && !hasOrderBy && !hasDocumentIdPrefix;
  }, [conditions, orConditions, orderBy, documentIdPrefix]);

  const executeTestRunRef = useRef(executeTestRun);
  executeTestRunRef.current = executeTestRun;
  useEffect(() => {
    if (
      !open ||
      !connectionId ||
      !collectionName ||
      (orConditions.length > 0 && orConditions.length < 2) ||
      isNativeQuery
    ) {
      return;
    }
    const timeoutId = window.setTimeout(() => {
      executeTestRunRef.current(connectionId, collectionName);
    }, 300);
    return () => window.clearTimeout(timeoutId);
  }, [open, connectionId, collectionName, queryDefSignature, isNativeQuery]);

  const previewQuery = buildQueryFromState(conditions, orderBy, limit, collectionName);
  const queryToSave = state && collectionName ? buildFirestoreQuery(state, collectionName) : null;
  const hasQueryToSave = Boolean(
    queryToSave &&
    ((queryToSave.filters?.length ?? 0) > 0 ||
      (queryToSave.orGroups?.length ?? 0) > 0 ||
      (queryToSave.orderBy?.length ?? 0) > 0 ||
      (queryToSave.documentIdPrefix?.trim() ?? '') !== '' ||
      (queryToSave.limit ?? 0) > 0)
  );
  const runDisabled = isRunQueryDisabled(
    loading,
    connectionId,
    collectionName,
    orConditions,
    testRunLoading,
    testRunError
  );
  const saveDisabled = testRunLoading || Boolean(testRunError) || !hasQueryToSave;
  const trimmedSaveQueryName = saveQueryName.trim();
  const isUpdateExistingQuery =
    Boolean(trimmedSaveQueryName) &&
    savedQueries.some((q) => q.name.trim() === trimmedSaveQueryName);
  const saveButtonLabel = isUpdateExistingQuery ? 'Update Query' : 'Save Query';
  const showOrError = isOrErrorRelevant(error);
  const hasSavedQueries = sortedSavedQueries.length > 0;
  const showNoResults =
    queryResults !== null && queryResults.documents.length === 0 && !loading && !error;

  useEffect(() => {
    if (queryResults && queryResults.documents.length > 0) {
      onOpenChange(false);
    }
  }, [queryResults, onOpenChange]);

  const handleRunQuery = async () => {
    if (!connectionId) {
      toast({
        variant: 'destructive',
        title: 'Error',
        description: 'No connection selected',
      });
      return;
    }
    if (!collectionName) {
      toast({
        variant: 'destructive',
        title: 'Error',
        description: 'No collection selected',
      });
      return;
    }
    await executeQuery(connectionId, collectionName);
  };

  const handleSaveQuery = () => {
    const trimmedName = saveQueryName.trim();
    if (!queryToSave || !hasQueryToSave) {
      toast({
        variant: 'destructive',
        title: 'Error',
        description: 'No query to save. Add filters, sorting, or a limit.',
      });
      return;
    }
    if (!connectionId || !collectionName) return;
    const name = trimmedName || defaultSaveQueryName();
    saveQuery(connectionId, collectionName, name, queryToSave);
    setSavedQueries(loadQueries(connectionId, collectionName));
    setSaveQueryName('');
  };

  const handleLoadQuery = (name: string) => {
    if (!connectionId || !collectionName) return;
    const query = loadQuery(connectionId, collectionName, name);
    if (!query) {
      toast({
        variant: 'destructive',
        title: 'Error',
        description: 'Query not found',
      });
      return;
    }
    applyQueryToCollection(collectionName, query);
    setSaveQueryName(name);
  };

  const handleDeleteSavedQuery = (name: string) => {
    if (!connectionId || !collectionName) return;
    deleteQuery(connectionId, collectionName, name);
    setSavedQueries(loadQueries(connectionId, collectionName));
  };

  const handleSetDefaultQuery = (name: string) => {
    if (!connectionId || !collectionName) return;
    setDefaultQuery(connectionId, collectionName, name);
    setSavedQueries(loadQueries(connectionId, collectionName));
  };

  const handleRemoveDefaultQuery = () => {
    if (!connectionId || !collectionName) return;
    clearDefaultQuery(connectionId, collectionName);
    setSavedQueries(loadQueries(connectionId, collectionName));
    toast({
      title: 'Success',
      description: 'Default query removed',
    });
  };

  const toggleQueryPreview = () => setQueryPreviewOpen((prev) => !prev);

  useEffect(() => {
    if (!open) return;
    const onKeyDown = (e: KeyboardEvent) => {
      if (e.key !== 'Escape') return;
      e.preventDefault();
      onOpenChange(false);
    };
    document.addEventListener('keydown', onKeyDown);
    return () => document.removeEventListener('keydown', onKeyDown);
  }, [open, onOpenChange]);

  return {
    open,
    onOpenChange,
    collectionName,
    availableFields,
    documentIdPrefix,
    onDocumentIdPrefixChange: (value: string) =>
      collectionName ? setDocumentIdPrefix(collectionName, value) : undefined,
    savedQueries: sortedSavedQueries,
    defaultQueryName,
    handleSetDefaultQuery,
    handleRemoveDefaultQuery,
    saveQueryName,
    setSaveQueryName,
    queryPreviewOpen,
    toggleQueryPreview,
    activeFilterCount,
    conditions,
    orConditions,
    orderBy,
    limit,
    error,
    queryResults,
    loading,
    testRunLoading,
    testRunError,
    previewQuery,
    runDisabled,
    saveDisabled,
    saveButtonLabel,
    showOrError,
    hasSavedQueries,
    showNoResults,
    activeQuery,
    addCondition: () => collectionName && addCondition(collectionName),
    removeCondition: (index: number) => collectionName && removeCondition(collectionName, index),
    updateCondition: (index: number, condition: Filter) =>
      collectionName && updateCondition(collectionName, index, condition),
    addOrCondition: () => collectionName && addOrCondition(collectionName),
    removeOrCondition: (index: number) =>
      collectionName && removeOrCondition(collectionName, index),
    updateOrCondition: (index: number, condition: Filter) =>
      collectionName && updateOrCondition(collectionName, index, condition),
    addOrderBy: () => collectionName && addOrderBy(collectionName),
    removeOrderBy: (index: number) => collectionName && removeOrderBy(collectionName, index),
    updateOrderBy: (index: number, order: OrderBy) =>
      collectionName && updateOrderBy(collectionName, index, order),
    setLimit: (newLimit: number) => collectionName && setLimit(collectionName, newLimit),
    handleClearQuery: () => {
      setSaveQueryName('');
      if (onClearQuery) onClearQuery();
      else if (collectionName) clearQuery(collectionName);
    },
    handleRunQuery,
    handleSaveQuery,
    handleLoadQuery,
    handleDeleteSavedQuery,
  };
}
